// Delegate — the headless hook moved to `@heidi/workspace/data` (D2,
// ARCFE-1062), the org-teams stayer whose D1 transport blocker
// (`sendBatchTeamInvitationsWithRoleIds` stranded in `src/api/scribeAPIV2_1.ts`)
// closed at ARCFE-1041.
//
// This file owns the app reactions (ARCFE-921): the intl'd destructive toast,
// the per-invitee Rematch `trackSendTeamInvitation` dispatch, and the COMMERCE
// `invalidateCurrentTeamBillingInfo()` call — the owning domain exports that
// command and app integration sequences it (delivery section 3); it is
// deliberately not injected through `configureWorkspaceData`.
//
// `teamName` and `source` stay HERE rather than crossing into the package: they
// exist only to feed the analytics dispatch, and this delegate already closes
// over them.
//
// `source` now ARRIVES as a growth-owned token and is bound to the app union
// here (openspec task 2.1b). `send_team_invitation` is a cross-domain event, so
// no domain may declare its name and the `TeamInviteSource` union stays app-side;
// what `@heidi/growth` owns is the two tokens its own entry points report, and
// this map is the one place they meet the union. `satisfies` makes it TOTAL by
// construction, so a token the union has no member for fails at the binding
// rather than at the first emit.
import { trackSendTeamInvitation } from '@/lib/analytics/scribe/ui';
import { type TeamInviteSource } from '@/models/analytics/features/ui';
import { type ErrorResponse } from '@/types';
import { toast } from '@heidi/design-system/toast';
import { TEAM_INVITE_SOURCE, type TeamInviteSourceToken } from '@heidi/growth/contracts';
import { useTranslations } from '@heidi/runtime/i18n';
import { useMutateSendBatchTeamInvites as useMutateSendBatchTeamInvitesCore } from '@heidi/workspace/data';
import { invalidateCurrentTeamBillingInfo } from '../query/useQueryTeamBilling';

// MODULE-PRIVATE, deliberately. Exporting it would add a new legacy symbol to
// the `workspace/organization-teams` family ratchet, and this map is not a
// surface — it is the binding this delegate applies on the way to the dispatch
// it already owns. The suite proves it by driving the delegate once per token
// and reading the emitted `source`, which is a stronger claim than reading the
// map: it also proves the map is the thing on the emit path.
const TEAM_INVITE_SOURCE_BY_TOKEN = {
  [TEAM_INVITE_SOURCE.practiceInviteModal]: 'practice_invite_modal',
  [TEAM_INVITE_SOURCE.onboardingInviteStep]: 'onboarding_invite_step',
} as const satisfies Record<TeamInviteSourceToken, TeamInviteSource>;

export function useMutateSendBatchTeamInvites(
  teamName: string,
  source?: TeamInviteSourceToken
) {
  const { t } = useTranslations();

  return useMutateSendBatchTeamInvitesCore({
    onInvitesSent: (data, variables) => {
      void invalidateCurrentTeamBillingInfo(variables.teamId);
      const totalEmailCount = variables.emails.length;
      variables.emails.forEach((email) => {
        trackSendTeamInvitation({
          teamId: variables.teamId,
          email: email,
          teamName: teamName,
          teamLink: data.success_emails?.[email],
          totalEmailCount,
          source: source && TEAM_INVITE_SOURCE_BY_TOKEN[source],
        });
      });
    },
    onSendInvitesError: (error) => {
      const errorData = error.response?.data as ErrorResponse;
      toast({
        tone: 'destructive',
        title: t('team.failedToSendTeamInvitation'),
        description: errorData.detail.msg,
      });
    },
  });
}
